<template>
  <b-modal
    :id="modalId"
    v-model="isOpen"
    size="lg"
    centered
    no-close-on-backdrop
    :title="title || 'Galeria de imagens'"
    @hide="closeModal"
  >
    <div class="xfield-image-picker">
      <p v-if="!items.length" class="xfield-image-picker-empty text-muted mb-3">
        Nenhuma imagem selecionada.
      </p>

      <div v-else class="xfield-image-picker-grid mb-3">
        <div
          v-for="(item, index) in items"
          :key="itemKey(item, index)"
          class="xfield-image-picker-item"
          :class="{
            dragging: dragIndex === index,
            'drag-over': dragOverIndex === index,
          }"
          draggable="true"
          @dragstart="onDragStart(index, $event)"
          @dragover.prevent="onDragOver(index)"
          @dragleave="onDragLeave(index)"
          @drop.prevent="onDrop(index)"
          @dragend="onDragEnd"
        >
          <img
            ref="thumbs"
            :src="itemPreview(item)"
            :alt="itemName(item, index)"
            class="xfield-image-picker-thumb"
            width="50"
            height="50"
            @load="onThumbLoad(item)"
            @error="onThumbError(item)"
          />
          <button
            type="button"
            class="xfield-image-picker-remove"
            :disabled="disabled"
            title="Remover imagem"
            @click.prevent="removeItem(index)"
          >
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
          <span class="xfield-image-picker-index">{{ index + 1 }}</span>
        </div>
      </div>

      <div class="xfield-image-picker-add">
        <label class="btn btn-outline-primary mb-0" :class="{ disabled }">
          <i class="fas fa-plus mr-1" aria-hidden="true"></i>
          Inserir imagens
          <input
            ref="fileInput"
            type="file"
            class="d-none"
            :accept="acceptAttr"
            multiple
            :disabled="disabled"
            @click="onFilePickStart"
            @change="onFilesSelected"
          />
        </label>
        <small v-if="acceptAttr" class="d-block text-muted mt-2">
          Tipos permitidos: {{ acceptAttr }}
        </small>
      </div>
    </div>

    <template #modal-footer>
      <b-button variant="secondary" @click="closeModal">Fechar</b-button>
      <b-button
        variant="primary"
        :disabled="applyDisabled"
        :title="
          hasPendingThumbnails
            ? 'Aguarde o carregamento das miniaturas'
            : null
        "
        @click="confirmAndClose"
      >
        Aplicar
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { fetchFileObjectUrl } from '@/store/utils/publicLogoFile'

export default {
  name: 'xfield-file-image-picker',

  props: {
    value: {
      type: [Array, String, Object],
      default: () => [],
    },
    mediaType: {
      type: String,
      default: 'image/jpeg;image/png',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Galeria de imagens',
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      items: [],
      remotePreviews: {},
      remoteLoading: {},
      dragIndex: null,
      dragOverIndex: null,
      localObjectUrls: [],
      loadedThumbs: {},
      remoteFailed: {},
      thumbsBusy: false,
    }
  },

  computed: {
    modalId() {
      return `xfield-file-image-picker-${this._uid}`
    },

    isOpen: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      },
    },

    acceptAttr() {
      const raw = String(this.mediaType || '')
        .split(/[;,]/)
        .map((part) => part.trim())
        .filter(Boolean)
      return raw.join(',')
    },

    allowedTypes() {
      return String(this.mediaType || '')
        .split(/[;,]/)
        .map((part) => part.trim().toLowerCase())
        .filter(Boolean)
    },

    hasPendingThumbnails() {
      return this.items.some((item) => this.isThumbPending(item))
    },

    applyDisabled() {
      console.log('applyDisabled', this.disabled, this.thumbsBusy, this.hasPendingThumbnails)
      return this.disabled || this.thumbsBusy || this.hasPendingThumbnails
    },
  },

  created() {
    // Fora do `data()` para o Vue 2 não observar/corrupter os File
    this._fileById = Object.create(null)
  },

  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.syncFromValue()
      }
    },
    items() {
      this.$nextTick(this.syncCachedThumbs)
    },
    remotePreviews: {
      deep: true,
      handler() {
        this.$nextTick(this.syncCachedThumbs)
      },
    },
  },

  beforeDestroy() {
    // Só revoga blob: de preview. NÃO apaga File — quem guarda o File
    // para upload é o EditXFieldModal (_imagePickerFiles).
    this.revokeLocalObjectUrls()
    this._fileById = Object.create(null)
  },

  methods: {
    open() {
      this.isOpen = true
    },

    closeModal() {
      this.isOpen = false
      this.dragIndex = null
      this.dragOverIndex = null
    },

    confirmAndClose() {
      if (this.applyDisabled) return
      this.emitValue()
      this.closeModal()
    },

    thumbKey(item) {
      if (item.kind === 'file') {
        return `file:${item.fileId || item.preview || item.name}`
      }
      return `url:${item.url || item.name}`
    },

    isThumbPending(item) {
      const key = this.thumbKey(item)
      if (this.loadedThumbs[key]) return false
      if (item.kind === 'url' && item.url && this.remoteFailed[item.url]) {
        return false
      }
      return true
    },

    refreshThumbsBusy() {
      this.thumbsBusy = this.items.some((item) => this.isThumbPending(item))
    },

    markThumbSettled(item) {
      const key = this.thumbKey(item)
      if (!this.loadedThumbs[key]) {
        this.$set(this.loadedThumbs, key, true)
      }
      this.refreshThumbsBusy()
    },

    onThumbLoad(item) {
      if (!this.itemPreview(item)) return
      this.markThumbSettled(item)
    },

    onThumbError(item) {
      if (item.kind === 'url' && item.url && this.remoteLoading[item.url]) return
      this.markThumbSettled(item)
    },

    syncCachedThumbs() {
      const thumbs = this.$refs.thumbs
      const list = !thumbs ? [] : Array.isArray(thumbs) ? thumbs : [thumbs]
      this.items.forEach((item, index) => {
        if (!this.itemPreview(item)) return
        const img = list[index]
        if (img && img.complete && img.naturalWidth > 0) {
          this.markThumbSettled(item)
        }
      })
      this.refreshThumbsBusy()
    },

    onFilePickStart() {
      if (this.disabled) return
      this.thumbsBusy = true
      const release = () => {
        window.removeEventListener('focus', release)
        window.setTimeout(() => {
          this.refreshThumbsBusy()
        }, 100)
      }
      window.addEventListener('focus', release)
    },

    rememberFile(file) {
      const id = `f_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
      this._fileById[id] = file
      return id
    },

    normalizeValue(raw) {
      if (raw == null || raw === '') return []
      if (Array.isArray(raw)) return raw.slice()
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw)
          return Array.isArray(parsed) ? parsed.slice() : raw ? [raw] : []
        } catch (error) {
          return raw ? [raw] : []
        }
      }
      return []
    },

    syncFromValue() {
      this.thumbsBusy = true
      this.revokeLocalObjectUrls()
      this.loadedThumbs = {}
      this._fileById = Object.create(null)
      this.items = this.normalizeValue(this.value)
        .map((item) => {
          if (item instanceof File || item instanceof Blob) {
            const preview = URL.createObjectURL(item)
            this.localObjectUrls.push(preview)
            const fileId = this.rememberFile(item)
            return {
              kind: 'file',
              fileId,
              preview,
              name: item.name,
            }
          }
          if (item && typeof item === 'object') {
            if (item.file instanceof File || item.file instanceof Blob) {
              const preview =
                item.preview || URL.createObjectURL(item.file)
              if (!item.preview) this.localObjectUrls.push(preview)
              const fileId = this.rememberFile(item.file)
              return {
                kind: 'file',
                fileId,
                preview,
                name: item.name || item.file.name,
              }
            }
            if (item.url || item.path || item.value) {
              const url = item.url || item.path || item.value
              this.ensureRemotePreview(url)
              return { kind: 'url', url, name: item.name || url }
            }
          }
          if (typeof item === 'string') {
            this.ensureRemotePreview(item)
            return { kind: 'url', url: item, name: item }
          }
          return null
        })
        .filter(Boolean)
      this.refreshThumbsBusy()
    },

    emitValue() {
      // Emite File reais vindos de _fileById (fora da reatividade do Vue).
      // Nunca JSON.stringify — File vira {}.
      const filesData = this.items
        .map((item) => {
          if (item.kind === 'file' && item.fileId) {
            const file = this._fileById[item.fileId]
            if (!(file instanceof File) && !(file instanceof Blob)) return null
            return {
              kind: 'file',
              file,
              name: item.name || file.name,
            }
          }
          if (item.kind === 'url' && item.url) {
            return {
              kind: 'url',
              url: item.url,
              name: item.name || item.url,
            }
          }
          return null
        })
        .filter(Boolean)

      this.$emit('setImagePickerValue', filesData)
      // v-model só com metadados/URLs — Files ficam no handler setImagePickerValue
      this.$emit(
        'input',
        filesData.map((item) =>
          item.kind === 'file'
            ? { kind: 'file', name: item.name, pending: true }
            : { kind: 'url', url: item.url, name: item.name }
        )
      )
    },

    itemKey(item, index) {
      if (item.kind === 'file') {
        return `file-${item.fileId || item.name}-${index}`
      }
      return `url-${item.url || index}-${index}`
    },

    itemName(item, index) {
      return item.name || `Imagem ${index + 1}`
    },

    itemPreview(item) {
      if (item.kind === 'file') return item.preview
      if (this.remotePreviews[item.url]) return this.remotePreviews[item.url]
      if (this.isDirectImageUrl(item.url)) {
        if (item.url.indexOf('google.com') > 0) {
          const match = /d\/([^/]+)\/view/.exec(item.url)
          if (match && match[1]) {
            return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w200`
          }
        }
        return item.url
      }
      return item.preview || ''
    },

    isDirectImageUrl(value) {
      if (!value || typeof value !== 'string') return false
      return (
        value.indexOf('http://') === 0 ||
        value.indexOf('https://') === 0 ||
        value.indexOf('blob:') === 0 ||
        value.indexOf('data:image') === 0
      )
    },

    async ensureRemotePreview(path) {
      if (!path || this.remotePreviews[path] || this.remoteLoading[path]) return
      if (this.isDirectImageUrl(path)) return

      this.$set(this.remoteLoading, path, true)
      this.$delete(this.remoteFailed, path)
      try {
        const objectUrl = await fetchFileObjectUrl(path)
        if (!objectUrl) {
          this.$set(this.remoteFailed, path, true)
          return
        }
        this.localObjectUrls.push(objectUrl)
        this.$set(this.remotePreviews, path, objectUrl)
      } catch (error) {
        console.warn('Não foi possível carregar preview da galeria:', path, error)
        this.$set(this.remoteFailed, path, true)
      } finally {
        this.$set(this.remoteLoading, path, false)
        this.refreshThumbsBusy()
      }
    },

    isAllowedFile(file) {
      if (!file) return false
      if (!this.allowedTypes.length) return true

      const type = String(file.type || '').toLowerCase()
      if (!type) {
        // Alguns browsers não preenchem file.type; libera por extensão
        const name = String(file.name || '').toLowerCase()
        return this.allowedTypes.some((allowed) => {
          if (allowed.indexOf('jpeg') >= 0 || allowed.indexOf('jpg') >= 0) {
            return name.endsWith('.jpg') || name.endsWith('.jpeg')
          }
          if (allowed.indexOf('png') >= 0) return name.endsWith('.png')
          if (allowed.indexOf('webp') >= 0) return name.endsWith('.webp')
          if (allowed.indexOf('gif') >= 0) return name.endsWith('.gif')
          return false
        })
      }

      return this.allowedTypes.some((allowed) => {
        if (allowed.endsWith('/*')) {
          return type.indexOf(allowed.replace('/*', '/')) === 0
        }
        if (allowed === 'image/jpg') return type === 'image/jpeg'
        if (allowed === 'image/jpeg') {
          return type === 'image/jpeg' || type === 'image/jpg'
        }
        return type === allowed
      })
    },

    onFilesSelected(event) {
      this.thumbsBusy = true
      const files = Array.from((event.target && event.target.files) || [])
      if (!files.length) {
        this.refreshThumbsBusy()
        return
      }

      const nextItems = this.items.slice()
      files.forEach((file) => {
        if (!this.isAllowedFile(file)) {
          if (this.$notify) {
            this.$notify({
              message: `Tipo de arquivo não permitido: ${file.name}`,
              type: 'warning',
              duration: 4000,
            })
          }
          return
        }
        const preview = URL.createObjectURL(file)
        this.localObjectUrls.push(preview)
        const fileId = this.rememberFile(file)
        nextItems.push({
          kind: 'file',
          fileId,
          preview,
          name: file.name,
        })
      })
      this.items = nextItems
      this.refreshThumbsBusy()

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    removeItem(index) {
      if (this.disabled) return
      const nextItems = this.items.slice()
      const [removed] = nextItems.splice(index, 1)
      this.items = nextItems
      if (removed && removed.kind === 'file' && removed.preview) {
        URL.revokeObjectURL(removed.preview)
        this.localObjectUrls = this.localObjectUrls.filter(
          (url) => url !== removed.preview
        )
      }
      this.refreshThumbsBusy()
    },

    onDragStart(index, event) {
      if (this.disabled) {
        event.preventDefault()
        return
      }
      this.dragIndex = index
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', String(index))
      }
    },

    onDragOver(index) {
      if (this.disabled || this.dragIndex == null) return
      this.dragOverIndex = index
    },

    onDragLeave(index) {
      if (this.dragOverIndex === index) {
        this.dragOverIndex = null
      }
    },

    onDrop(index) {
      if (this.disabled || this.dragIndex == null || this.dragIndex === index) {
        this.onDragEnd()
        return
      }
      const next = this.items.slice()
      const [moved] = next.splice(this.dragIndex, 1)
      next.splice(index, 0, moved)
      this.items = next
      this.onDragEnd()
    },

    onDragEnd() {
      this.dragIndex = null
      this.dragOverIndex = null
    },

    revokeLocalObjectUrls() {
      this.localObjectUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url)
        } catch (error) {}
      })
      this.localObjectUrls = []
      this.remotePreviews = {}
      this.remoteLoading = {}
      this.remoteFailed = {}
      this.loadedThumbs = {}
    },
  },
}
</script>

<style scoped>
.xfield-image-picker-empty {
  font-size: 0.9rem;
}

.xfield-image-picker-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.xfield-image-picker-item {
  position: relative;
  width: 50px;
  height: 50px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  background: #f8fafc;
  cursor: grab;
  user-select: none;
}

.xfield-image-picker-item.dragging {
  opacity: 0.45;
}

.xfield-image-picker-item.drag-over {
  outline: 2px solid #12576e;
  outline-offset: 2px;
}

.xfield-image-picker-thumb {
  display: block;
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 5px;
}

.xfield-image-picker-remove {
  position: absolute;
  top: -7px;
  right: -7px;
  width: 18px;
  height: 18px;
  border: 0;
  border-radius: 50%;
  background: #b11b1b;
  color: #fff;
  font-size: 10px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
}

.xfield-image-picker-remove:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.xfield-image-picker-index {
  position: absolute;
  left: 2px;
  bottom: 2px;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  border-radius: 4px;
  background: rgba(18, 87, 110, 0.85);
  color: #fff;
  font-size: 9px;
  line-height: 14px;
  text-align: center;
}

.xfield-image-picker-add .disabled {
  pointer-events: none;
  opacity: 0.6;
}
</style>
